import { Fragment, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';

/**
 * Agreement text, rendered as React elements.
 *
 * ── WHY NOT A MARKDOWN LIBRARY ───────────────────────────────────────
 *
 * The documents are written by us and use five things: headings, paragraphs,
 * bulleted lists, pipe tables and bold. A library that handles all of
 * CommonMark would be most of this portal's JavaScript, and every one of them
 * renders to an HTML string that then needs `dangerouslySetInnerHTML` and a
 * sanitiser in front of it. Building elements directly means there is no
 * string of HTML anywhere, so there is nothing to sanitise: a `<script>` in an
 * agreement prints as the characters `<script>`.
 *
 * Anything outside that set renders as plain text rather than failing, so a
 * document that grows a feature this does not know degrades into readable
 * prose, not a blank pane.
 *
 * ── THE FIRST `#` HEADING IS DROPPED ─────────────────────────────────
 *
 * Each document opens with its own title, and the gate already names it
 * directly above the text. Two copies of "Ridgewill Terms of Service" one line
 * apart reads as a rendering fault.
 */
export function Markdown({ source }: { source: string }) {
  return <>{blocks(source.replace(/\r\n/g, '\n'))}</>;
}

function blocks(src: string): ReactNode[] {
  const lines = src.split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let droppedTitle = false;
  let key = 0;
  const L = (n: number) => (lines[n] ?? '').trim();

  while (i < lines.length) {
    const t = L(i);
    if (!t) {
      i++;
      continue;
    }

    const h = /^(#{1,4})\s+(.*)$/.exec(t);
    if (h) {
      i++;
      const level = (h[1] ?? '').length;
      if (level === 1 && !droppedTitle) {
        droppedTitle = true;
        continue;
      }
      out.push(heading(level, inline(h[2] ?? ''), key++));
      continue;
    }

    if (/^\|/.test(t)) {
      const rows: string[] = [];
      while (i < lines.length && /^\|/.test(L(i))) rows.push(L(i++));
      out.push(table(rows, key++));
      continue;
    }

    if (/^[-*]\s+/.test(t)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(L(i))) {
        items.push(L(i++).replace(/^[-*]\s+/, ''));
      }
      out.push(
        <ul key={key++} style={st.list}>
          {items.map((it, n) => (
            <li key={n} style={st.item}>
              {inline(it)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(t)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(L(i))) {
        items.push(L(i++).replace(/^\d+\.\s+/, ''));
      }
      out.push(
        <ol key={key++} style={st.list}>
          {items.map((it, n) => (
            <li key={n} style={st.item}>
              {inline(it)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    // A paragraph runs until a blank line or the start of another block.
    const para: string[] = [];
    while (i < lines.length) {
      const p = L(i);
      if (!p || /^(#{1,4}\s|\||[-*]\s|\d+\.\s)/.test(p)) break;
      para.push(p);
      i++;
    }
    out.push(
      <p key={key++} style={st.p}>
        {inline(para.join(' '))}
      </p>,
    );
  }
  return out;
}

function heading(level: number, children: ReactNode, key: number) {
  // Inside the gate the document sits under an h2 (its name), so its own
  // sections start at h3. Levels are shifted, not reused, so the outline a
  // screen reader announces stays nested.
  if (level <= 2) return <h3 key={key} style={st.h2}>{children}</h3>;
  return <h4 key={key} style={st.h3}>{children}</h4>;
}

function table(rows: string[], key: number) {
  const cells = (r: string) =>
    r
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
  const isRule = (r: string) => /^\|?\s*:?-{2,}/.test(r);
  const head = cells(rows[0] ?? '');
  const body = rows.slice(1).filter((r) => !isRule(r)).map(cells);
  return (
    <div key={key} style={st.tableWrap}>
      <table style={st.table}>
        <thead>
          <tr>
            {head.map((c, n) => (
              <th key={n} style={st.th}>
                {inline(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, n) => (
            <tr key={n}>
              {r.map((c, m) => (
                <td key={m} style={st.td}>
                  {inline(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Bold, italic, code and links. Links only for https and mailto: an agreement
 * has no business pointing at `javascript:`, and an unrecognised scheme prints
 * as text rather than becoming a live href.
 */
function inline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`|(?<![*\w])\*([^*\s][^*]*?)\*(?!\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) parts.push(<strong key={k++}>{inline(m[1])}</strong>);
    else if (m[2] !== undefined) {
      const href = m[3] ?? '';
      const safe = /^(https:|mailto:)/i.test(href);
      parts.push(
        safe ? (
          <a key={k++} href={href} target="_blank" rel="noreferrer" style={{ color: C.brand }}>
            {m[2]}
          </a>
        ) : (
          <Fragment key={k++}>{m[2]}</Fragment>
        ),
      );
    } else if (m[4] !== undefined) parts.push(<code key={k++}>{m[4]}</code>);
    else if (m[5] !== undefined) parts.push(<em key={k++}>{m[5]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

const st = {
  p: { margin: '0 0 .85rem', lineHeight: 1.6 },
  h2: { fontSize: '1rem', fontWeight: 700, margin: '1.75rem 0 .6rem', lineHeight: 1.35 },
  h3: { fontSize: '.95rem', fontWeight: 600, margin: '1.25rem 0 .5rem', lineHeight: 1.35 },
  list: { margin: '0 0 .85rem', paddingLeft: '1.25rem', lineHeight: 1.6 },
  item: { marginBottom: '.3rem' },
  tableWrap: { overflowX: 'auto', margin: '0 0 1rem' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: '.9rem', lineHeight: 1.45 },
  th: {
    textAlign: 'left',
    fontWeight: 600,
    padding: '.45rem .6rem',
    borderBottom: `1px solid ${C.inputBorder}`,
    verticalAlign: 'bottom',
  },
  td: { padding: '.45rem .6rem', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' },
} satisfies Record<string, React.CSSProperties>;
